import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  Issue,
  IssueType,
  replaceMentionPlaceholderWithNames,
  Severity,
  useCurrentAccount,
  useFindByQuery,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import {
  TypeDropdown,
  variantToSeverity,
  type TypeVariant,
} from '../crop-circle/Components/Type';
import { Icon } from '../crop-circle/icons';
import { borderRadius } from '../crop-circle/tokens/borderRadius';
import { shadows } from '../crop-circle/tokens/shadows';
import LoadingIndicator from '../loadingIndicator';
import { IssueHeaderOld } from './issueHeader-old';
import { IssueMenu } from './issueMenu';
import { ReminderSummary } from './reminderSummary';

// Mapping between Severity and TypeVariant for crop circle redesign
const severityToVariant: Record<Severity, TypeVariant> = {
  [Severity.Red]: 'urgent',
  [Severity.Yellow]: 'pending',
  [Severity.Green]: 'solved',
  [Severity.Purple]: 'note',
  [Severity.Blue]: 'routines',
};

export type IssueHeaderProps = {
  issue: Issue;
  onChanged: (severity: string) => void;
  setShowFooter: (show: boolean) => void;
  titleStyle?: StyleProp<ViewStyle>;
  viewAssetInfo?: () => void;
  onIssueDelete: () => void;
  hideMenu?: boolean;
  onPrint?: () => Promise<void>;
  onBackPress?: () => void;
  onAssetPress?: () => void;
};

const IssueHeaderBase = (props: IssueHeaderProps) => {
  const { t } = useTranslation();
  const isDesktop = () => Platform.OS === 'web';
  const issue = props.issue;
  const { account } = useCurrentAccount();
  const isRestrictedUser =
    account?.effectivePermissions?.includes('*.restricted');

  const getProcessedTitle = () => {
    const titleText = issue.title;

    // Process mentions to ensure clean format
    if (titleText.includes('@[') && (issue as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (issue as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const issueId = issue.id;

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': issueId },
    };
  }, [issueId]);

  const { data: workflows, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const hasReminder = useMemo(() => workflows?.items?.length > 0, [workflows]);

  const workflow = useMemo(() => workflows?.items[0], [workflows]);

  const [currentSeverity, setSeverity] = useState(issue.severity);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const handleBackPress = () => {
    if (props.onBackPress) {
      props.onBackPress();
    }
  };

  const handleAssetPress = () => {
    if (props.onAssetPress) {
      props.onAssetPress();
    } else if (props.viewAssetInfo && issue.asset) {
      props.viewAssetInfo();
    }
  };

  if (!issue) {
    return;
  }

  return (
    <View style={styles.container}>
      {/* Header Bar: Activities and Asset Navigation */}
      {!isDesktop() && (
        <View style={styles.headerBar}>
          {props.onBackPress ? (
            <TouchableOpacity
              style={styles.headerButton}
              onPress={handleBackPress}
              activeOpacity={0.7}
            >
              <Icon name="Chevron Left" size={20} color={Colors.black} />
              <Text style={styles.headerButtonText}>
                {t('activity.menu.titlePlural')}
              </Text>
            </TouchableOpacity>
          ) : null}

          {issue.asset && (
            <TouchableOpacity
              style={styles.headerButton}
              onPress={handleAssetPress}
              activeOpacity={0.7}
            >
              <Text style={styles.headerButtonText}>
                {t('assets.menu.titleSingular')}
              </Text>
              <Icon name="Chevron Right" size={20} color={Colors.black} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Card */}
      <View
        style={{
          ...styles.cardContainer,
          ...(isDesktop()
            ? styles.cardContainerDesktop
            : styles.cardContainerMobile),
        }}
      >
        <View
          style={{
            ...styles.card,
            ...(isDesktop() ? styles.cardDesktop : styles.cardMobile),
          }}
        >
          {/* Left: TypeDropdown */}
          <View style={styles.leftSection}>
            <TypeDropdown
              value={severityToVariant[currentSeverity]}
              onChange={handleSeverityChange}
              disabled={isSeverityDisabled()}
              showLabel={false}
              testID="issue-detail-severity-dropdown"
              availableTypes={['urgent', 'pending', 'solved']}
            />
          </View>

          {/* Middle: Title and Subtitle */}
          <View style={styles.middleSection}>
            <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
              {getProcessedTitle()}
            </Text>
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace.name}
            </Text>
          </View>

          {/* Right: Settings Icon with Menu */}
          <View style={styles.rightSection}>
            {!props.hideMenu && (
              <View style={styles.menuContainer}>
                {issue.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.black}
                    size={20}
                    style={styles.pinnedIcon}
                  />
                )}
                <IssueMenu
                  issue={issue}
                  hasReminder={hasReminder}
                  onIssueDelete={props.onIssueDelete}
                  onPrint={props.onPrint}
                />
              </View>
            )}
          </View>
        </View>
      </View>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        !isRestrictedUser &&
        workflow && <ReminderSummary workflow={workflow} issue={props.issue} />
      )}
    </View>
  );
};

export const IssueHeader = (props: IssueHeaderProps) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueHeaderOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  return <IssueHeaderBase {...props} />;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    zIndex: 10,
    ...Platform.select({
      web: {
        zIndex: 1,
      },
    }),
  },
  headerBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.black,
  },
  cardContainer: {
    borderWidth: 0,
    overflow: 'visible',
  },
  cardContainerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  cardContainerDesktop: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 72,
    gap: 12,
    borderWidth: 0,
    overflow: 'visible',
    zIndex: 10,
    ...shadows.sm,
  },
  cardMobile: {
    borderRadius: borderRadius.xl,
    borderBottomWidth: 0,
    backgroundColor: Colors.primaryGrayBackground,
    marginBottom: 0,
  },
  cardDesktop: {
    borderRadius: 0,
    borderBottomWidth: 1,
    borderColor: Colors.primaryGrayBackground,
    backgroundColor: Colors.white,
    marginBottom: 16,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    zIndex: 20,
  },
  middleSection: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minWidth: 0,
    marginLeft: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    height: '100%',
    marginTop: 3,
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
